import html2pdf from 'html2pdf.js';
import moment from "moment";
import sealImage from "../assets/img/seal.png";
import sealLogo from "../assets/img/sealLogo.png";

// Driver Service & Clearance letter. Reuses the EXACT visual template of the
// verification letter (driverLetterGenerator.js) — same header/logo, divider,
// title, ref/date row, seal/signature and CSS — but swaps in the clearance
// content modelled on the standard sub-city clearance request: registration
// period, total amount earned, and service termination date. Non-English
// versions keep the English field keywords in parentheses ((NAME), (TIN),
// (PLATE NUMBER), (FROM), (UPTO), (AMOUNT), (TERMINATION DATE)) so the letter
// stays unambiguous for the receiving office, exactly like the source sample.

const fmtDate = (value) => (value ? moment(value).format("MMMM DD, YYYY") : "____________");
// Addis Ababa wall time (the page sends "YYYY-MM-DDTHH:mm" in Addis time) on a
// 12-hour clock, as every time staff read (owner rule): "October 28, 2026, 2:30 pm".
const fmtDateTime = (value) => (value ? moment(value).format("MMMM DD, YYYY, h:mm a") : "____________");

const getClearanceContent = (driver, data, language) => {
  const driverName = `${driver.firstName || ""} ${driver.lastName || ""}`.trim();
  const tin = driver.tin || data.tin || "____________";
  const plate = driver.vehicleNumber || data.plate || "____________";
  const subCity = data.subCity || "____________";
  const city = data.city || "Addis Ababa";
  const from = fmtDate(data.fromDate);
  const upto = fmtDate(data.uptoDate);
  const amount = (data.amount !== undefined && data.amount !== null && `${data.amount}`.trim() !== "")
    ? `${data.amount}`
    : "____________";
  const termination = fmtDateTime(data.terminationDate);

  const content = {
    en: {
      title: "DRIVER SERVICE & CLEARANCE LETTER",
      dateLabel: "Date:",
      refLabel: "Ref No:",
      date: moment().format("MMMM DD, YYYY"),
      toLine1: `To: ${subCity} Sub City`,
      city,
      subject: "Subject: Response to the Requested Clearance Letter",
      body1: `With reference to the subject mentioned above, Mr./Mrs./Ms. (NAME) ${driverName}, registered under (TIN) ${tin} with vehicle (PLATE NUMBER) ${plate}, has been registered and rendering transport services on the Donkey Drive platform from (FROM) ${from} up to (UPTO) ${upto}.`,
      body2: `We would like to notify the concerned offices that the total amount earned from this service is (AMOUNT) ${amount} Birr.`,
      body3: `We further confirm and notify the concerned offices that the driver discontinued the service as of (TERMINATION DATE) ${termination}.`,
      closing: "With regards,"
    },
    am: {
      title: "የሹፌር አገልግሎትና ማጣራት ደብዳቤ",
      dateLabel: "ቀን:",
      refLabel: "ማጣቀሻ ቁጥር:",
      date: moment().format("MMMM DD, YYYY"),
      toLine1: `ለ ${subCity} ክፍለ ከተማ`,
      city,
      subject: "ጉዳዩ፡- ደብዳቤ እንዲጻፍ ለቀረበው ጥያቄ መሰረት መልስ ስለ መስጠት",
      body1: `ከላይ በርዕሱ ለመጥቀስ እንደተጠቀሰው ወ/ሮ/አቶ (NAME) ${driverName} የሚባሉት በ(TIN) ${tin} በታርጋ ቁጥር (PLATE NUMBER) ${plate} ዶንኪ ድራይቭ የትራንስፖርት ስራዎች ላይ ተመዝግበው እየሰሩ ሲሆን ከ(FROM) ${from} እስከ (UPTO) ${upto}`,
      body2: `ድረስ ጠቅላላ የሰሩት የገንዘብ መጠን (AMOUNT) ${amount} ብር መሆኑን ለሚመለከታቸው አካላት ለማሳወቅ እንወዳለን።`,
      body3: `በ(TERMINATION DATE) ${termination} አገልግሎታቸውን ማቋረጣቸውን ለሚመለከታቸው አካላት ለማሳወቅ እንወዳለን።`,
      closing: "ከሰላምታ ጋር,"
    },
    om: {
      title: "XALAYAA TAJAAJILAA FI RAGAA BILISUMMAA KONKOLAACHISAA",
      dateLabel: "Guyyaa:",
      refLabel: "Lakk. Wabii:",
      date: moment().format("MMMM DD, YYYY"),
      toLine1: `Gara ${subCity} Kutaa Magaalaa`,
      city,
      subject: "Dhimma: Gaaffii Xalayaa barbaadameef deebii kennuu",
      body1: `Akkuma mata duree olitti ibsametti, Obbo/Aaddee (NAME) ${driverName} kan (TIN) ${tin} fi (PLATE NUMBER) ${plate} qaban, waltajjii Donkey Drive irratti tajaajila geejjibaa kennaa kan turan yoo ta'u (FROM) ${from} hanga (UPTO) ${upto}`,
      body2: `tajaajila kana irraa galii waliigalaa argatan (AMOUNT) ${amount} Birrii ta'uu isaa qaamota dhimmi ilaalu beeksisuu barbaanna.`,
      body3: `(TERMINATION DATE) ${termination} irraa eegalee tajaajila isaanii akka addaan kutan qaamota dhimmi ilaalu beeksisuu barbaanna.`,
      closing: "Nagaa wajjin,"
    },
    ti: {
      title: "ደብዳቤ ኣገልግሎትን ምጽራይን ሾፈር",
      dateLabel: "ዕለት:",
      refLabel: "ቁጽሪ ማጣቀሲ:",
      date: moment().format("MMMM DD, YYYY"),
      toLine1: `ናብ ${subCity} ክፍለ ከተማ`,
      city,
      subject: "ጉዳይ፡ ንዝቐረበ ሕቶ ደብዳቤ መሰረት መልሲ ምሃብ",
      body1: `ከምቲ ኣብ ላዕሊ ኣርእስቲ ዝተጠቕሰ፣ ኣይተ/ወይዘሮ (NAME) ${driverName} ዝበሃሉ በ(TIN) ${tin} ብታርጋ ቁጽሪ (PLATE NUMBER) ${plate} ኣብ መድረኽ ዶንኪ ድራይቭ ኣገልግሎት መጓዓዝያ ተመዝጊቦም ክሰርሑ ጸኒሖም ካብ (FROM) ${from} ክሳብ (UPTO) ${upto}`,
      body2: `ጠቕላላ ዘእተዉዎ መጠን ገንዘብ (AMOUNT) ${amount} ብር ምዃኑ ንዝምልከቶም ኣካላት ከነፍልጥ ንፈቱ።`,
      body3: `ካብ (TERMINATION DATE) ${termination} ኣትሒዞም ኣገልግሎቶም ከም ዘቋረጹ ንዝምልከቶም ኣካላት ከነፍልጥ ንፈቱ።`,
      closing: "ምስ ሰላምታ,"
    },
    ar: {
      title: "خطاب الخدمة وإخلاء الطرف للسائق",
      dateLabel: "التاريخ:",
      refLabel: "رقم المرجع:",
      date: moment().format("MMMM DD, YYYY"),
      toLine1: `إلى: ${subCity} (المدينة الفرعية)`,
      city,
      subject: "الموضوع: الرد على طلب خطاب إخلاء الطرف",
      body1: `بالإشارة إلى الموضوع المذكور أعلاه، فإن السيد/السيدة (NAME) ${driverName}، المسجل برقم (TIN) ${tin} ولوحة المركبة (PLATE NUMBER) ${plate}، كان مسجلاً ويقدم خدمات النقل على منصة دونكي درايف من (FROM) ${from} إلى (UPTO) ${upto}.`,
      body2: `نود إبلاغ الجهات المعنية بأن إجمالي المبلغ المكتسب من هذه الخدمة هو (AMOUNT) ${amount} بر.`,
      body3: `كما نؤكد ونبلغ الجهات المعنية بأن السائق قد أوقف الخدمة اعتباراً من (TERMINATION DATE) ${termination}.`,
      closing: "مع خالص التحيات،"
    }
  };

  return content[language] || content.en;
};

export const generateDriverClearanceLetter = async (driver, clearanceData, language) => {
  const content = getClearanceContent(driver, clearanceData, language);

  // Unique clearance letter number (mirrors the DL- scheme of the verification letter).
  const letterNumber = clearanceData.refNo ||
    `DCL-${moment().format('YYYYMMDD')}-${driver.uid?.substring(0, 6) || Math.random().toString(36).substring(2, 8).toUpperCase()}`;

  const htmlTemplate = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Noto+Sans+Ethiopic:wght@400;700&family=Noto+Sans+Arabic:wght@400;700&family=Roboto:wght@400;700&display=swap');

        body {
          font-family: ${language === 'am' || language === 'ti' ? "'Noto Sans Ethiopic', Arial, sans-serif" : language === 'ar' ? "'Noto Sans Arabic', Arial, sans-serif" : "'Roboto', Arial, sans-serif"};
          font-size: 12px;
          line-height: 1.4;
          color: #000;
          margin: 0;
          padding: 20px;
          direction: ${language === 'ar' ? 'rtl' : 'ltr'};
        }

        .header {
          text-align: center;
          margin-bottom: 30px;
          position: relative;
        }

        .logo {
          width: 120px;
          height: 120px;
          margin: 0 auto 10px;
        }

        .company-name {
          font-size: 24px;
          font-weight: bold;
          margin-bottom: 5px;
        }

        .company-info {
          font-size: 12px;
          color: #666;
          margin-bottom: 5px;
        }

        .divider {
          border-top: 1px solid #000;
          margin: 10px 0;
        }

        .title {
          font-size: 18px;
          font-weight: bold;
          text-align: center;
          margin-bottom: 20px;
          text-decoration: underline;
        }

        .date-ref {
          display: flex;
          justify-content: space-between;
          margin-bottom: 20px;
        }

        .to {
          font-weight: bold;
          margin-bottom: 4px;
        }

        .to-city {
          margin-bottom: 16px;
        }

        .subject {
          font-weight: bold;
          text-decoration: underline;
          text-align: center;
          margin: 20px 0;
        }

        .content {
          margin-bottom: 15px;
          text-align: justify;
        }

        .signature-section {
          margin-top: 30px;
          position: relative;
        }

        .closing {
          margin-left: 4px;
          margin-bottom: 5px;
        }

        .seal {
          width: 100px;
          height: 100px;
          margin-left: 0;
        }

        .footer {
          position: fixed;
          bottom: 20px;
          left: 0;
          right: 0;
          text-align: center;
          font-size: 10px;
          color: #666;
          margin-top: 40px;
        }

        @media print {
          body { margin: 0; padding: 15px; }
          .footer { position: absolute; }
        }
      </style>
    </head>
    <body>
      <div class="header">
        ${sealLogo ? `<img src="${sealLogo}" alt="Logo" class="logo">` : ''}
        <div class="company-name">DONKEY DRIVE</div>
        <div class="company-info">Haset Information Technology PLC</div>
        <div class="company-info">Email: info@donkeydrive.io | Phone: 9260</div>
      </div>

      <div class="divider"></div>

      <div class="title">${content.title}</div>

      <div class="date-ref">
        <span>${content.dateLabel} ${content.date}</span>
        <span>${content.refLabel} ${letterNumber}</span>
      </div>

      <div class="to">${content.toLine1}</div>
      <div class="to-city">${content.city}</div>

      <div class="subject">${content.subject}</div>

      <div class="content">${content.body1}</div>

      <div class="content">${content.body2}</div>

      <div class="content">${content.body3}</div>

      <div class="signature-section">
        <div class="closing">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;${content.closing}</div>
        ${sealImage ? `<img src="${sealImage}" alt="Seal" class="seal">` : '<div style="margin-left: 0; font-size: 10px; color: #666;">[STAMP]</div>'}
      </div>

    </body>
    </html>
  `;

  const options = {
    margin: 0.5,
    filename: `Driver_Clearance_${driver.firstName}_${driver.lastName}_${moment().format("YYYYMMDD")}.pdf`,
    image: { type: 'jpeg', quality: 0.98 },
    html2canvas: {
      scale: 2,
      useCORS: true,
      letterRendering: true
    },
    jsPDF: {
      unit: 'in',
      format: 'a4',
      orientation: 'portrait'
    }
  };

  const element = document.createElement('div');
  element.innerHTML = htmlTemplate;

  try {
    await html2pdf().set(options).from(element).save();
    // Return the ref number printed on the letter so the caller can log the
    // exact same identifier to the audit trail.
    return letterNumber;
  } catch (error) {
    console.error('Error generating clearance PDF:', error);
    throw new Error('Failed to generate clearance letter. Please try again.');
  }
};
